import React from 'react'
import { observer } from 'mobx-react'
import { ImageComponent } from 'src/views/components'
import { multipleSubscriptionContainer } from 'src/models'
import { i18nTranslate, isINMarket } from 'src/utils'
import { SubscriptionShippingAddressDetail } from './SubscriptionShippingAddressDetail'
import ViewCartRefreshIcon from 'src/views/components/Icons/ViewCartRefreshIcon'
import { InfoIcon } from 'src/views/components/Icons/InfoIcon'
import { PaymentIcon } from 'src/views/components/PaymentIcon'
import './styles.scss'

@observer
class ExistingSubscriptionDetails extends React.Component {
  handleSubscriptionOptionsChange = (id = '') => {
    this.props.handleSubscriptionOptionChange(id)
  }

  getOrdinalSuffix = (day = '') => {
    if (!day) return ''
    let suffix = 'th'
    if (day % 10 === 1 && day % 100 !== 11) suffix = 'st'
    else if (day % 10 === 2 && day % 100 !== 12) suffix = 'nd'
    else if (day % 10 === 3 && day % 100 !== 13) suffix = 'rd'
    return `${day}${suffix}`
  }

  renderSubscriptionPaymentDetails = (payment = {}) => {
    const paymentProperties = payment?.paymentProperties ?? []
    const getPropertyValue = propertyName => {
      return (
        paymentProperties?.find(property => property?.name === propertyName)
          ?.value ?? ''
      )
    }

    const firstName = getPropertyValue('firstName')
    const cardNumber = getPropertyValue('cardNumber')
    const cardType = getPropertyValue('cardType')

    if (!cardNumber) {
      return <></>
    }

    return (
      <div
        className="subscription-payment-details"
        data-testid="qa-subscription-payment-details">
        <span className="payment-icon-wrapper mr-2">
          <PaymentIcon type={cardType?.toLowerCase()} width="24" height="24" />
        </span>
        <span className="payment-first-name">{firstName}</span>
        <span className="payment-card-number ml-1">**** {cardNumber}</span>
      </div>
    )
  }

  renderSubscriptionAddress = (addressInfo = {}) => {
    return <SubscriptionShippingAddressDetail addressDetails={addressInfo} />
  }

  renderProductImage = (subscription = {}) => {
    const totalCount = subscription?.itemInfo?.length || 0
    const productImageMaxCount = 2
    const remainingProductCount = totalCount - productImageMaxCount
    const imageMoreTitle = i18nTranslate(
      'subscriptionSummaryCard.quantityOfAdditionalItemsLabel',
      '%%quantity%% more',
      { isNextGenTranslations: true }
    ).replace('%%quantity%%', `${remainingProductCount}`)

    return subscription?.itemInfo?.map((item, index) => {
      const itemCount = index + 1
      if (itemCount < 3) {
        const mainProduct =
          multipleSubscriptionContainer.getDefaultProductSku(item)?.[0]
        const {
          primaryimage = '',
          imageURL = '',
          name = '',
        } = mainProduct?.skuProperties || {}

        return (
          <div
            className="subscription-option-details-image"
            key={name}
            data-testid="qa-subscription-product-image">
            <ImageComponent
              width="62px"
              height="62px"
              optimizeImage={true}
              src={primaryimage || imageURL}
              alt={name}
              className={`mw-100 mh-100 prod-img`}
              data-testid="qa-user-sub-product-image"
            />
          </div>
        )
      } else if (itemCount == 3) {
        return (
          <div
            className="subscription-image-place-holder"
            key={'image-holder'}
            data-testid="qa-subscription-image-place-holder">
            <span>{imageMoreTitle}</span>
          </div>
        )
      }
    })
  }

  renderTotalItemCount = (totalItemCount = 0) => {
    const totalItemsText = i18nTranslate(
      'subscriptionSummaryCard.totalItemsCaption',
      'Total items',
      { isNextGenTranslations: true }
    )
    return (
      <div
        className="subscription-total-item-wrapper"
        data-testid="qa-subscription-product-count">
        <span>
          <ViewCartRefreshIcon
            className="count-refresh-icon"
            width="9"
            height="9"
            fillColor="#252525"
          />
        </span>
        <span>{totalItemsText}</span>
        <span className="mr-1">:</span>
        <span>{totalItemCount}</span>
      </div>
    )
  }

  renderSubscriptionGroupDetails = (subscription = {}) => {
    const {
      recurrenceOrderProcessingDate = '',
      name = '',
      id = '',
      itemInfo = [],
    } = subscription || {}

    const processingDay =
      this.getOrdinalSuffix(recurrenceOrderProcessingDate) || ''
    const totalItemCount = itemInfo?.length || 0

    const processingDayTitle = i18nTranslate(
      'subscriptionManageHeader.processingDay',
      'Processing Day',
      { isNextGenTranslations: true }
    )
    const subscriptionIdTitle = i18nTranslate(
      'subscriptionSummaryCard.subscriptionIdCaption',
      'Subscription ID',
      { isNextGenTranslations: true }
    )

    return (
      <div className="subscription-group-details">
        <div
          className="subscription-processing-day-wrapper"
          data-testid="qa-subscription-processing-day">
          <span className="processing-day-title">{processingDayTitle}</span>
          <span className="processing-day-value">{processingDay}</span>
        </div>
        <div
          className="subscription-name-wrapper"
          data-testid="qa-subscription-group-name">
          {name}
        </div>
        <div
          className="subscription-id-wrapper"
          data-testid="qa-subscription-group-id">
          <span>{subscriptionIdTitle}</span>
          <span className="ml-2">#{id}</span>
        </div>
        {this.renderTotalItemCount(totalItemCount)}
      </div>
    )
  }

  renderSubscriptionGroupUnavailableText = (unavailableMessage = '') => {
    return (
      <div className="subscription-group-unavailable-text">
        {unavailableMessage}
        <span className="info-icon-wrapper">
          <InfoIcon
            className="info-icon"
            width="24"
            height="24"
            fill="#3783B9"
          />
        </span>
      </div>
    )
  }

  renderExistingSubscriptionDetails1 = (
    subscription = {},
    isToDisableOption = false
  ) => {
    const { addressInfo = {}, payment = {} } = subscription
    const disabledClass = isToDisableOption
      ? 'disable-subscription-details'
      : ''

    return (
      <div className={`subscription-option-details ${disabledClass}`}>
        {this.renderSubscriptionGroupDetails(subscription)}
        <div className="subscription-image-wrapper">
          {this.renderProductImage(subscription)}
        </div>
        {this.renderSubscriptionAddress(addressInfo)}
        {this.renderSubscriptionPaymentDetails(payment)}
      </div>
    )
  }

  renderExistingSubscriptionRadioOption = (subscription = {}) => {
    const { name = '', id = '' } = subscription
    let isCartItemAlreadySubscribed =
      multipleSubscriptionContainer.checkIsSomeCartItemAlreadyInSubscribedGroup(
        subscription
      )

    // let isSubscriptionDateAlreadyProcessed =
    //   multipleSubscriptionContainer.checkIfProcessingDaySameAsCurrentDay(
    //     subscription
    //   )

    const paymentInProcess = subscription?.properties?.paymentInProcess
    const isPaymentInProcess =
      paymentInProcess === true || paymentInProcess === 'true'

    const recurrenceOrderPaymentInProcess =
      subscription?.properties?.recurrenceOrderPaymentInProcess
    const isRecurrenceOrderPaymentInProcess =
      recurrenceOrderPaymentInProcess === true ||
      recurrenceOrderPaymentInProcess === 'true'

    const isIndiaMarket = isINMarket()
    const subscriptionStatus = subscription?.status || ''
    const isPaymentApprovalPending =
      isIndiaMarket && subscriptionStatus === 'PENDING'

    const isToDisableOption =
      isCartItemAlreadySubscribed ||
      isPaymentInProcess ||
      isRecurrenceOrderPaymentInProcess ||
      isPaymentApprovalPending

    const disabledInputClass = isToDisableOption ? 'disable-input-label' : ''

    const alreadySubscribedMessage = i18nTranslate(
      'cartCreateNewSubscriptionComponent.addToSubscription.itemAlreadySubscribed',
      'Already subscribed. Manage this product in the subscription dashboard.',
      { isNextGenTranslations: true }
    )

    const paymentInProcessMessage = i18nTranslate(
      'multipleSubscription.paymentInProcess',
      'Your payment approval is currently pending. Subscription changes are unavailable until the approval is completed.',
      { isNextGenTranslations: true }
    )

    // const dateProcessedMessage = i18nTranslate(
    //   'multipleSubscription.dateProcessedMessage',
    //   'This subscription is currently being processed and can't be edited'
    // )

    const isAnyPaymentInProcess =
      isPaymentInProcess ||
      isRecurrenceOrderPaymentInProcess ||
      isPaymentApprovalPending

    const unavailableMessage = isAnyPaymentInProcess
      ? paymentInProcessMessage
      : alreadySubscribedMessage

    return (
      <>
        <input
          type="radio"
          name="multiSubscriptionOption"
          aria-label={name}
          key={'option' + id}
          id={'option' + id}
          onChange={() => {
            this.handleSubscriptionOptionsChange(id)
          }}
          disabled={isToDisableOption}
          checked={
            !isAnyPaymentInProcess &&
            multipleSubscriptionContainer.selectedSubscriptionOption === id
          }
          data-testId="qa-existing-subscription-option"
        />
        <label
          htmlFor={'option' + id}
          className={`subscription-option-details-wrapper ${disabledInputClass}`}>
          {this.renderExistingSubscriptionDetails1(
            subscription,
            isToDisableOption
          )}
          {isToDisableOption &&
            this.renderSubscriptionGroupUnavailableText(unavailableMessage)}
        </label>
      </>
    )
  }

  renderExistingSubscriptionList = (subscriptionList = []) => {
    return (
      subscriptionList?.length > 0 &&
      subscriptionList?.map?.(subscription => {
        return this.renderExistingSubscriptionRadioOption(subscription)
      })
    )
  }

  render() {
    const { subscriptionList = [] } = this.props
    return (
      <div className="existing-subscription-list-wrapper">
        {this.renderExistingSubscriptionList(subscriptionList)}
      </div>
    )
  }
}

export { ExistingSubscriptionDetails }
export default ExistingSubscriptionDetails
